/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, useEffect } from 'react';
import { TextInput } from '../input/TextInput/TextInput';
import { Reprice, LimitAmount, InputContainer, Spinner } from './RefreshableCurrencyInput.styles';
import { Button } from '../Button/DefaultButton/Button';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { formatNumber, convertToNumber } from '@/utils/formatValues';
import { handleDrawAmount } from '@/utils/handleDrawAmount';
import { executeAction } from '@/utils/handleEndpoints';
import _ from 'lodash';
import { setVmoInputValue, setIsAtLeastOneRepriceDone, setIsChange } from '@/reducers/VmoV2';

interface RefreshableCurrencyInputPros {
  limitAmount: string;
  typeOfAmount?: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
  auxMinValue?: number;
  auxMaxValue?: number;
  auxValue?: number;
  setIsReprice: React.Dispatch<React.SetStateAction<boolean>>;
  onValidChange?: (isValid: boolean) => void;
}

const RefreshableCurrencyInput: React.FC<RefreshableCurrencyInputPros> = ({
  setIsReprice,
  limitAmount,
  typeOfAmount,
  variant = 'primary',
  auxMinValue = 50000,
  auxMaxValue = 100000,
  onValidChange,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const templateConfig = useAppSelector((state) => state.TemplateConfig);
  const loanApplication = useAppSelector((state) => state.LoanApplication);
  const inputValue = useAppSelector((state) => state.VmoV2.vmoInputValue);
  const isAtLeastOneRepriceDone = useAppSelector((state) => state.VmoV2.isAtLeastOneRepriceDone);
  const isICE = templateConfig.isICE ?? false;
  const typeOfProgram = loanApplication.programType === 'HEL' ? (isICE ? 'helRates' : 'helData') : isICE ? 'helocRates' : 'helocData';
  const isDrawAmount = typeOfAmount === 'INITIAL DRAW';
  const auxLoanApplication = _.cloneDeep(loanApplication);
  const isChange = useAppSelector((state) => state.VmoV2.isChange);

  const isOnRange = (value: string) => {
    const numericValue = convertToNumber(value.replace(/[$,]/g, ''));
    if (isNaN(auxMinValue) || isNaN(auxMaxValue) || auxMinValue === 0 || auxMaxValue === 0) {
      return false;
    }

    return numericValue < Number(auxMinValue) || numericValue > Number(auxMaxValue);
  };

  const handleInputChange = (value: string | number, _propertyType?: any, _index?: number) => {
    setIsReprice(false);
    const stringValue = value.toString();

    if (stringValue === '' || stringValue === '$') {
      dispatch(setVmoInputValue('$0'));
      if (onValidChange) onValidChange(false);
      dispatch(setIsChange(true));
      return;
    }

    const numericOnly = stringValue.replace(/[^0-9]/g, '');
    const cleanValue = numericOnly.replace(/^0+/, '');

    // If empty after cleaning, set to $0
    if (!cleanValue) {
      dispatch(setVmoInputValue('$0'));
      if (onValidChange) onValidChange(false);
      dispatch(setIsChange(true));
      return;
    }

    const formattedValue = formatNumber(Number(cleanValue));
    dispatch(setVmoInputValue(formattedValue));

    const numericValue = Number(cleanValue);

    //validate only if there are valid min/max bounds
    const valid = isNaN(auxMinValue) || isNaN(auxMaxValue) || auxMinValue === 0 || auxMaxValue === 0 ? true : numericValue >= auxMinValue && numericValue <= auxMaxValue;

    if (onValidChange) onValidChange(valid);
    dispatch(setIsChange(!valid));
  };

  const onBlur = () => {
    const numericValue = convertToNumber(inputValue.replace(/[$,]/g, ''));
    let adjustedValue = inputValue;

    if (!isNaN(auxMinValue) && !isNaN(auxMaxValue) && auxMinValue > 0 && auxMaxValue > 0) {
      if (numericValue > auxMaxValue) {
        adjustedValue = formatNumber(auxMaxValue);
      } else if (numericValue < auxMinValue) {
        adjustedValue = formatNumber(auxMinValue);
      } else {
        adjustedValue = formatNumber(numericValue);
      }
    } else {
      adjustedValue = formatNumber(numericValue);
    }

    dispatch(setVmoInputValue(adjustedValue));
    dispatch(setIsChange(false));

    if (isDrawAmount) {
      const cleanedString = adjustedValue.replace(/[$,]/g, '');
      const numberValue = parseFloat(cleanedString);
      auxLoanApplication.drawAmount = Math.trunc(numberValue / 100) * 100;
      auxLoanApplication.minDrawAmount = auxLoanApplication.minLineAmount;
      dispatch({
        type: 'LoanApplication/updateValue',
        payload: { ...auxLoanApplication },
      });
    } else if (typeOfAmount === 'LOAN AMOUNT') {
      const cleanedString = adjustedValue.replace(/[$,]/g, '');
      const numberValue = parseFloat(cleanedString);
      auxLoanApplication.loanAmount = Math.trunc(numberValue / 100) * 100;
      dispatch({
        type: 'LoanApplication/updateValue',
        payload: { ...auxLoanApplication },
      });
    }
  };

  useEffect(() => {
    if (!isAtLeastOneRepriceDone) {
      dispatch(setVmoInputValue('$0'));
      if (onValidChange) onValidChange(false);
      dispatch(setIsChange(true));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const HandleRepriceRates = async () => {
    setLoading(true);
    if (setIsReprice) setIsReprice(false);
    // Set the reprice flag to true when reprice button is hit
    dispatch({ type: 'CurrentPage/setHasRepriced', payload: true });
    const cleanedString = inputValue.replace(/[$,]/g, '');
    const numberValue = parseFloat(cleanedString);
    const isReprice = true;

    if (typeOfAmount === 'LINE AMOUNT' || typeOfAmount === 'LOAN AMOUNT') {
      auxLoanApplication.lineAmount = Math.trunc(numberValue / 100) * 100;
      if (isICE) {
        auxLoanApplication.loanAmount = numberValue;
      }
    } else {
      auxLoanApplication.drawAmount = Math.trunc(numberValue / 100) * 100;
      auxLoanApplication.minDrawAmount = auxLoanApplication.lineAmount;
    }

    const drawAmountValues = handleDrawAmount(numberValue, auxLoanApplication);
    dispatch({
      type: 'LoanApplication/updateValue',
      payload: { ...drawAmountValues },
    });

    try {
      const response = await executeAction(isICE, auxLoanApplication, dispatch, templateConfig, isReprice);
      setLoading(false);

      if (response && 'data' in response) {
        // For ICE, check if we have the data in the expected program type or fallback to available data
        let responseData;
        if (isICE) {
          const data = response?.data;
          const requestedProgram = data?.[typeOfProgram];

          // If the requested program type has data, use it
          if (requestedProgram && requestedProgram.length > 0) {
            responseData = requestedProgram[0];
          }
          // Otherwise, check if we have data in helRates or helocRates
          else if (data?.helRates && data.helRates.length > 0) {
            responseData = data.helRates[0];
          } else if (data?.helocRates && data.helocRates.length > 0) {
            responseData = data.helocRates[0];
          }
          // Last resort: use data directly
          else {
            responseData = data;
          }
        } else {
          responseData = response?.data[typeOfProgram]?.rates[0];
        }

        if (!responseData) {
          console.error('No response data found for reprice');
          setLoading(false);
          return;
        }

        const updateProgram = auxLoanApplication.selectedProgram;
        const estimatedMonthly = Math.round(parseFloat(responseData.estimatedMonthlyPayment || '0'));
        updateProgram.estimatedMonthlyPayment = estimatedMonthly.toString();

        if (isICE) {
          updateProgram.loanAmount = responseData.loanAmount.toString();
          auxLoanApplication.apr = responseData.apr;
        } else {
          updateProgram.lineAmount = responseData.lineAmount;
          updateProgram.maxAmountApproved = responseData.maxAmountApproved;
          updateProgram.maxDrawAmount = responseData.maxDrawAmount;
          updateProgram.maxLineAmount = responseData.maxLineAmount;
          updateProgram.minDrawAmount = responseData.minDrawAmount;
          updateProgram.minLineAmount = responseData.minLineAmount;
        }
        updateProgram.apr = responseData.apr;
        setLoading(false);
        if (setIsReprice) {
          setIsReprice(true);
          dispatch(setIsAtLeastOneRepriceDone(true));
        }
        if (onValidChange) onValidChange(true);
        dispatch({
          type: 'LoanApplication/updateValue',
          payload: { ...auxLoanApplication },
        });
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const isButtonDisabled = loading || isOnRange(inputValue) || isChange;

  return (
    <Reprice>
      <InputContainer $isDrawAmount={isDrawAmount} style={{ gap: '0.5rem', alignItems: 'center' }} onBlur={onBlur}>
        <TextInput
          placeholder={'$0'}
          dataTestId="refreshable-currency-input"
          propertyType={'remaining'}
          onChange={handleInputChange}
          value={inputValue}
          padding={'1.375rem 0.5rem'}
        />
        {!isDrawAmount && <Button label={!loading ? 'Reprice' : <Spinner />} variant={variant} onClick={HandleRepriceRates} isDisabled={isButtonDisabled} />}
      </InputContainer>
      <LimitAmount>{limitAmount}</LimitAmount>
    </Reprice>
  );
};

export default RefreshableCurrencyInput;
